import React, { useEffect, useMemo, useRef, useState } from "react";

import "./Patients.css";

import Sidebar from "../../Components/Sidebar/Sidebar";
import LabelHeader from "../../Components/Commons/LabelHeader";
import TextField from "../../Components/Commons/Forms/TextField";
import SelectField from "../../Components/Commons/Forms/SelectField";
import BtnSave from "../../Components/Commons/Buttons/BtnSave";
import ProfilePictureUpload from "../ProfilePictureUpload/ProfilePictureUpload";
import DateField from "../Commons/Forms/DateField";
import BtnDelete from "../Commons/Buttons/BtnDelete";

import { Col, Row } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import {
  editAPatient,
  getPatientID,
} from "../Helpers/API/PatientApi/PatientApi";
import { validatePatientProfile } from "../Helpers/Utilities/Validations/PatientValidations";
import { toast } from "react-toastify";
import { Button, Modal, Radio, Select } from "antd";
import countryList from "react-select-country-list";
import {
  nationalities,
  phCitiesList,
  phProvincesList,
} from "../../Helpers/Utils/Common";
import { PhoneInput } from "react-international-phone";
import { getAge } from "../Helpers/Utilities/Common";
import { CameraOutlined, SettingOutlined } from "@ant-design/icons";
import Webcam from "react-webcam";

function EditPatient() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isClicked, setIsClicked] = useState(false);
  const [editPatient, setEditPatient] = useState([]);
  const [fileList, setFileList] = useState(null);
  const [isError, setError] = useState({
    last_name: false,
    first_name: false,
    birthdate: false,
    address: false,
    sex: false,
  });
  const countryOptions = useMemo(
    () =>
      countryList()
        .getData()
        .map((c) => ({
          value: c?.label?.toUpperCase(),
          label: c.label?.toUpperCase(),
        })),
    []
  );
  const [isCameraModalVisible, setIsCameraModalVisible] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [availableCameras, setAvailableCameras] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState(null);
  const webcamRef = useRef(null);
  const [patientPhoto, setPatientPhoto] = useState(null);

  // Get available cameras
  useEffect(() => {
    const getCameras = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter(
          (device) => device.kind === "videoinput"
        );
        setAvailableCameras(videoDevices);

        // Try to find external webcam (often has label containing "USB" or "External")
        const externalWebcam = videoDevices.find(
          (device) =>
            device.label.toLowerCase().includes("usb") ||
            device.label.toLowerCase().includes("external") ||
            device.label.toLowerCase().includes("logitech") || // Common webcam brands
            device.label.toLowerCase().includes("c920") ||
            !device.label.toLowerCase().includes("facetime") // Exclude built-in MacBook camera
        );

        if (externalWebcam) {
          setSelectedCamera(externalWebcam.deviceId);
        } else if (videoDevices.length > 0) {
          setSelectedCamera(videoDevices[0].deviceId);
        }
      } catch (error) {
        console.error("Error getting cameras:", error);
      }
    };

    if (isCameraModalVisible) {
      getCameras();
    }
  }, [isCameraModalVisible]);

  const capturePhoto = () => {
    const imageSrc = webcamRef.current.getScreenshot();
    setCapturedImage(imageSrc);
  };

  const retakePhoto = () => {
    setCapturedImage(null);
  };

  const usePhoto = () => {
    if (capturedImage) {
      // Convert base64 to blob for file upload
      fetch(capturedImage)
        .then((res) => res.blob())
        .then((blob) => {
          const file = new File([blob], "patient-photo.jpg", {
            type: "image/jpeg",
          });
          handleSaveImage(file);
          setCapturedImage(capturedImage);
          setPatientPhoto(capturedImage);
        });
    }
    setIsCameraModalVisible(false);
  };

  const videoConstraints = {
    width: 1280,
    height: 810,
    facingMode: "user",
    deviceId: selectedCamera ? { exact: selectedCamera } : undefined,
  };

  const handleCameraChange = (deviceId) => {
    setSelectedCamera(deviceId);
  };

  async function fetchPatient() {
    const response = await getPatientID(id, "", "");
    if (response?.data) {
      const patient = response?.data?.data[0];
      setEditPatient({
        ...patient,
        is_pwd: patient?.pwd_id && patient.pwd_id.trim() !== "" ? "1" : "0",
      });
      setPatientPhoto(response?.data?.data[0]?.file_url);
    } else {
      setEditPatient([]);
    }
  }

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    if (name === "email") {
      setEditPatient((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    } else {
      setEditPatient((prevState) => ({
        ...prevState,
        [name]: value?.toUpperCase(),
      }));
    }
  };

  const handleAddPhoneChange = (e, name) => {
    setEditPatient((prevState) => ({
      ...prevState,
      [name]: e,
    }));
  };

  const isMinor = getAge(editPatient.birthdate) < 18;
  const emergencyContactRequired =
    isMinor &&
    (!editPatient.emergency_contact?.trim() ||
      !editPatient.relation_w_contact?.trim() ||
      !editPatient.emergency_contact_no?.trim());

  async function handleSubmit() {
    setIsClicked(true);
    if (validatePatientProfile(editPatient, setError) && !isClicked) {
      const response = await editAPatient(editPatient, id, fileList);
      if (response.data) {
        toast.success(response.data.data.message);
        fetchPatient();
        setTimeout(() => {
          navigate(-1);
        }, 2000);
      } else if (response.error) {
        response.error.data.messages.error_messages.forEach((errMsg) =>
          toast.error(errMsg)
        );
      }
    }
    setIsClicked(false);
  }

  const handleSaveImage = (data) => {
    setFileList(data);
    setEditPatient((prevState) => ({
      ...prevState,
      profile_picture_id: null,
    }));
  };

  React.useEffect(() => {
    fetchPatient();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="App">
      <Sidebar active={"Patients"} />
      <div className="profile-container col p-5 pt-4">
        <LabelHeader text={"EDIT PATIENT"} />
        <div className="pt-5 ml-7 edit-patient-container">
          <label className="col-6 font-bold patient-details-heading">
            PATIENT DETAILS
          </label>
          <Row className="mt-2">
            <Col sm={3}>
              {/* <ProfilePictureUpload
                imageUrl={editPatient?.profile_picture}
                imageFile={handleSaveImage}
              /> */}
              <ProfilePictureUpload
                imageFile={handleSaveImage}
                capturedImage={patientPhoto}
              />
              <div style={{ marginTop: "10px" }}>
                <Button
                  type="primary"
                  icon={<CameraOutlined />}
                  onClick={() => setIsCameraModalVisible(true)}
                  style={{ width: "100%" }}
                >
                  Take Photo
                </Button>
              </div>
            </Col>
            <Col sm={9}>
              <Row>
                <Col>
                  <TextField
                    required
                    id="last_name"
                    name="last_name"
                    formLabel={`Last Name`}
                    size={`sm`}
                    onChange={handleEditChange}
                    value={editPatient?.last_name}
                    isError={isError.last_name}
                    errorMessage="Last name is required."
                    disabled={isClicked}
                  />
                </Col>
                <Col>
                  <TextField
                    required
                    id="first_name"
                    name="first_name"
                    formLabel={`First Name`}
                    size={`sm`}
                    onChange={handleEditChange}
                    value={editPatient?.first_name}
                    isError={isError.first_name}
                    errorMessage="First name is required."
                    disabled={isClicked}
                  />
                </Col>
                <Col>
                  {/* <TextField
                    id="middle_name"
                    name="middle_name"
                    formLabel={`MIDDLE NAME`}
                    size={`sm`}
                    onChange={handleEditChange}
                    value={editPatient?.middle_name || ""}
                    disabled={isClicked}
                  /> */}
                  <TextField
                    required
                    id="middle_name"
                    name="middle_name"
                    formLabel={
                      <div className="flex items-center">
                        <span className="me-3">Middle Name</span>{" "}
                        {/* adds space before checkbox */}
                        <input
                          type="checkbox"
                          checked={editPatient?.middle_name === "N/A"}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setEditPatient((prev) => ({
                                ...prev,
                                middle_name: "N/A",
                              }));
                            } else {
                              setEditPatient((prev) => ({
                                ...prev,
                                middle_name: "",
                              }));
                            }
                          }}
                        />
                        <span className="ms-1">N/A</span>
                      </div>
                    }
                    size="sm"
                    value={editPatient?.middle_name}
                    onChange={handleEditChange}
                    disabled={editPatient?.middle_name === "N/A"}
                  />
                </Col>
                <Col>
                  <TextField
                    id="suffix"
                    name="suffix"
                    formLabel={`Suffix`}
                    size={`sm`}
                    onChange={handleEditChange}
                    value={editPatient?.suffix || ""}
                    disabled={isClicked}
                  />
                </Col>
              </Row>
              <Row
                className={
                  editPatient?.country?.toUpperCase() === "PHILIPPINES"
                    ? "mb-4"
                    : ""
                }
              >
                {/* <Col>
                  <TextField
                    required
                    id="address"
                    name="address"
                    formLabel={`ADDRESS`}
                    size={`sm`}
                    onChange={handleEditChange}
                    value={editPatient?.address}
                    isError={isError.address}
                    errorMessage="Address is required."
                    disabled={isClicked}
                  />
                </Col> */}
                <Col>
                  <div className="row">
                    <span>
                      Country <label className="red"> *</label>
                    </span>
                    <Select
                      className="custom-select-height w-full"
                      value={editPatient?.country}
                      placeholder="Select Country"
                      options={countryOptions}
                      size="small"
                      onChange={(value) =>
                        handleEditChange({
                          target: {
                            name: "country",
                            value: value.toUpperCase(),
                          },
                        })
                      }
                      showSearch
                    />
                  </div>
                </Col>
                {editPatient?.country?.toUpperCase() !== "PHILIPPINES" && (
                  <>
                    <div className="col-sm-4">
                      <TextField
                        required
                        id="province"
                        name="province"
                        formLabel="State/Province/Region"
                        size="sm"
                        value={editPatient?.province}
                        onChange={handleEditChange}
                        isError={isError.province}
                        errorMessage="State/Province/Region is required."
                      />
                    </div>
                  </>
                )}
                {editPatient?.country?.toUpperCase() === "PHILIPPINES" && (
                  <>
                    <Col>
                      <div className="row">
                        <span>
                          Province <label className="red"> *</label>
                        </span>
                        <Select
                          placeholder="SELECT"
                          className="custom-select-height w-full"
                          value={editPatient?.province}
                          options={phProvincesList}
                          size="small"
                          onChange={(value) =>
                            handleEditChange({
                              target: { name: "province", value },
                            })
                          }
                          showSearch
                        />
                      </div>
                    </Col>
                  </>
                )}
                {editPatient?.country?.toUpperCase() !== "PHILIPPINES" && (
                  <>
                    <Col>
                      <TextField
                        required
                        id="city"
                        name="city"
                        formLabel="City/Town"
                        size="sm"
                        value={editPatient?.city}
                        onChange={handleEditChange}
                        isError={isError.city}
                        errorMessage="City/Town is required."
                      />
                    </Col>
                  </>
                )}
                {editPatient?.country?.toUpperCase() === "PHILIPPINES" && (
                  <>
                    <Col>
                      <div className="row">
                        <span>
                          City <label className="red"> *</label>
                        </span>
                        <Select
                          placeholder="SELECT"
                          className="custom-select-height w-full"
                          value={editPatient?.city}
                          options={phCitiesList}
                          size="small"
                          onChange={(value) =>
                            handleEditChange({
                              target: { name: "city", value },
                            })
                          }
                          showSearch
                        />
                      </div>
                    </Col>
                  </>
                )}
              </Row>
              <Row>
                {editPatient?.country?.toUpperCase() === "PHILIPPINES" && (
                  <>
                    <Col xs={4}>
                      <TextField
                        required
                        id="barangay"
                        name="barangay"
                        formLabel="Barangay"
                        size="sm"
                        value={editPatient?.barangay}
                        onChange={handleEditChange}
                        isError={isError.barangay}
                        errorMessage="Barangay is required."
                      />
                    </Col>
                  </>
                )}
                {editPatient?.country?.toUpperCase() === "PHILIPPINES" && (
                  <>
                    <Col>
                      <TextField
                        required
                        id="street"
                        name="street"
                        formLabel="Street"
                        size="sm"
                        value={editPatient?.street}
                        onChange={handleEditChange}
                        isError={isError.Street}
                        errorMessage="street is required."
                      />
                    </Col>
                  </>
                )}
                {editPatient?.country?.toUpperCase() !== "PHILIPPINES" && (
                  <>
                    <Col>
                      <TextField
                        required
                        id="street"
                        name="street"
                        formLabel="Street"
                        size="sm"
                        value={editPatient?.street}
                        onChange={handleEditChange}
                        isError={isError.Street}
                        errorMessage="street is required."
                      />
                    </Col>
                  </>
                )}
                <Col xs={3}>
                  <TextField
                    required
                    id="zip_code"
                    name="zip_code"
                    formLabel="Zip Code"
                    size="sm"
                    value={editPatient?.zip_code}
                    onChange={handleEditChange}
                    isError={isError.zip_code}
                    errorMessage="Zip Code is required."
                  />
                </Col>
              </Row>
              <Row>
                <Col sm={4}>
                  <DateField
                    required
                    id="birthdate"
                    name="birthdate"
                    formLabel={`Birthdate (MM/DD/YYYY)`}
                    size={`sm`}
                    onChange={handleEditChange}
                    value={editPatient?.birthdate}
                    isError={isError.birthdate}
                    errorMessage="Birthdate is required."
                    disabled={isClicked}
                  />
                </Col>
                <Col sm={4}>
                  {/* <SelectField
                    required
                    formLabel={`Sex`}
                    name="sex"
                    size={`sm`}
                    placeholder={"SELECT"}
                    value={editPatient?.sex}
                    options={[
                      { value: "", label: "SELECT" },
                      { value: "male", label: "MALE" },
                      { value: "female", label: "FEMALE" },
                    ]}
                    isError={isError.sex}
                    errorMessage="Sex is required."
                    onChange={handleEditChange}
                    disabled={isClicked}
                  /> */}
                  <div className="row">
                    <span>
                      Sex <label className="red"> *</label>
                    </span>
                    <Select
                      placeholder="SELECT"
                      className="custom-select-height w-full"
                      value={editPatient?.sex}
                      options={[
                        { value: "male", label: "MALE" },
                        { value: "female", label: "FEMALE" },
                      ]}
                      size="small"
                      onChange={(value) =>
                        handleEditChange({
                          target: { name: "sex", value },
                        })
                      }
                    />
                  </div>
                </Col>
                <Col sm={4}>
                  {/* <SelectField
                    formLabel={`STATUS`}
                    name="status"
                    size={`sm`}
                    placeholder={"SELECT"}
                    value={editPatient?.status}
                    options={[
                      { value: "", label: "SELECT" },
                      { value: "divorced", label: "DIVORCED" },
                      { value: "married", label: "MARRIED" },
                      { value: "separated", label: "SEPARATED" },
                      { value: "single", label: "SINGLE" },
                      { value: "widowed", label: "WIDOWED" },
                      { value: "child", label: "CHILD" },
                    ]}
                    onChange={handleEditChange}
                    disabled={isClicked}
                  /> */}
                  <div className="row">
                    <span>
                      Status <label className="red"> *</label>
                    </span>
                    <Select
                      placeholder="SELECT"
                      className="custom-select-height w-full"
                      value={editPatient?.civil_status}
                      options={[
                        { value: "child", label: "CHILD" },
                        { value: "single", label: "SINGLE" },
                        { value: "married", label: "MARRIED" },
                        { value: "divorced", label: "DIVORCED" },
                        { value: "separated", label: "SEPARATED" },
                        { value: "widowed", label: "WIDOWED" },
                      ]}
                      size="small"
                      onChange={(value) =>
                        handleEditChange({
                          target: { name: "civil_status", value },
                        })
                      }
                    />
                  </div>
                </Col>
              </Row>
              <Row>
                <Col sm={4}>
                  {/* <TextField
                    id="phone_no"
                    name="phone_no"
                    formLabel={`CONTACT NO.`}
                    size={`sm`}
                    onChange={handleEditChange}
                    value={editPatient?.contact_no}
                    disabled={isClicked}
                  /> */}
                  <span>
                    Mobile Number <label className="red"> *</label>
                  </span>
                  <PhoneInput
                    id="contact_no"
                    name="contact_no"
                    defaultCountry="ph"
                    value={editPatient?.contact_no || ""}
                    // onChange={(value) => {
                    //   console.log(value);
                    //   const safeValue = typeof value === "string" ? value : ""; // ensure string
                    //   const phoneValue = safeValue.replace(/\D/g, ""); // only digits

                    //   if (phoneValue.length <= 3) {
                    //     // Country code only or empty
                    //     handleEditChange({
                    //       target: { name: "contact_no", value: "" },
                    //     });
                    //   } else {
                    //     handleEditChange({
                    //       target: { name: "contact_no", value: safeValue },
                    //     });
                    //   }
                    // }}
                    onChange={(e) => handleAddPhoneChange(e, "contact_no")}
                    style={{
                      width: "100%",
                      height: "32px",
                    }}
                    inputStyle={{
                      width: "100%",
                      height: "32px",
                      border: "1px solid #d9d9d9",
                    }}
                    countrySelectorStyleProps={{
                      style: {
                        height: "32px",
                      },
                      buttonStyle: {
                        height: "32px",
                        padding: "0 8px",
                      },
                    }}
                  />
                </Col>
                <Col sm={4}>
                  <TextField
                    id="occupation"
                    name="occupation"
                    formLabel={`Occupation`}
                    size={`sm`}
                    onChange={handleEditChange}
                    value={editPatient?.occupation}
                    disabled={isClicked}
                  />
                </Col>
                <Col sm={4}>
                  <div className="row">
                    <span>Nationality</span>
                    <Select
                      className="custom-select-height w-full"
                      value={editPatient?.nationality}
                      placeholder="Select Nationality"
                      options={nationalities}
                      size="small"
                      onChange={(value) =>
                        handleEditChange({
                          target: { name: "nationality", value },
                        })
                      }
                      showSearch
                    />
                  </div>
                </Col>
              </Row>
              <Row>
                <Col sm={4}>
                  <TextField
                    id="religion"
                    name="religion"
                    formLabel="Religion"
                    size="sm"
                    value={editPatient?.religion}
                    onChange={handleEditChange}
                  />
                </Col>
                <Col sm={4}>
                  <TextField
                    id="email"
                    name="email"
                    formLabel={`Email`}
                    size={`sm`}
                    onChange={handleEditChange}
                    value={editPatient?.email}
                    disabled={isClicked}
                  />
                </Col>
                <Col sm={4}>
                  <TextField
                    required={getAge(editPatient?.birthdate) < 18}
                    id="emergency_contact"
                    name="emergency_contact"
                    formLabel="Emergency Contact Person"
                    size="sm"
                    onChange={handleEditChange}
                    value={editPatient?.emergency_contact}
                  />
                </Col>
              </Row>
              <Row>
                <Col sm={4}>
                  <TextField
                    required={getAge(editPatient?.birthdate) < 18}
                    id="relation_w_contact"
                    name="relation_w_contact"
                    formLabel="Relationship"
                    size="sm"
                    onChange={handleEditChange}
                    value={editPatient?.relation_w_contact}
                  />
                </Col>
                <Col sm={4}>
                  <span>
                    Emergency Contact Number{" "}
                    {getAge(editPatient?.birthdate) < 18 && (
                      <label className="red">*</label>
                    )}
                  </span>
                  <PhoneInput
                    id="emergency_contact_no"
                    name="emergency_contact_no"
                    defaultCountry="ph"
                    value={editPatient?.emergency_contact_no || ""}
                    // onChange={(value) => {
                    //   console.log(value);
                    //   const safeValue = typeof value === "string" ? value : ""; // ensure string
                    //   const phoneValue = safeValue.replace(/\D/g, ""); // only digits

                    //   if (phoneValue.length <= 3) {
                    //     // Country code only or empty
                    //     handleEditChange({
                    //       target: { name: "emergency_contact_no", value: "" },
                    //     });
                    //   } else {
                    //     handleEditChange({
                    //       target: {
                    //         name: "emergency_contact_no",
                    //         value: safeValue,
                    //       },
                    //     });
                    //   }
                    // }}
                    // onChange={handleEditChange}
                    onChange={(e) =>
                      handleAddPhoneChange(e, "emergency_contact_no")
                    }
                    style={{
                      width: "100%",
                      height: "32px",
                    }}
                    inputStyle={{
                      width: "100%",
                      height: "32px",
                      border: "1px solid #d9d9d9",
                    }}
                    countrySelectorStyleProps={{
                      style: {
                        height: "32px",
                      },
                      buttonStyle: {
                        height: "32px",
                        padding: "0 8px",
                      },
                    }}
                  />
                </Col>
                <Col>
                  <TextField
                    id="senior_id"
                    name="senior_id"
                    formLabel="Senior ID"
                    size="sm"
                    value={editPatient?.senior_id}
                    onChange={handleEditChange}
                    disabled={!(getAge(editPatient?.birthdate) >= 60)}
                  />
                </Col>
              </Row>
              <Row>
                <Col xs={4}>
                  <p className="form-label align-left">
                    is PWD <span className="badge">*</span>
                  </p>
                  <Radio.Group
                    className="w-full"
                    name="is_pwd"
                    value={editPatient?.is_pwd}
                    onChange={handleEditChange}
                  >
                    <Radio value="1">Yes</Radio>
                    <Radio value="0">No</Radio>
                  </Radio.Group>
                </Col>
                <Col xs={4}>
                  <TextField
                    id="pwd_id"
                    name="pwd_id"
                    formLabel="PWD ID"
                    size="sm"
                    value={editPatient?.pwd_id}
                    onChange={handleEditChange}
                    disabled={editPatient?.is_pwd === "0"}
                  />
                </Col>
              </Row>
            </Col>
          </Row>
          <div className="row justify-content-end mt-5 mb-3">
            <div className="col-2">
              <BtnDelete
                label={"Close"}
                size={"sm"}
                onClick={() => navigate(-1)}
                isClicked={isClicked}
              />
            </div>
            <div className="col-2">
              <BtnSave
                label={"Save changes"}
                size={"sm"}
                onClick={handleSubmit}
                isClicked={isClicked}
                disabled={
                  // !editPatient ||
                  // Object.values(editPatient?.last_name || "").every(
                  //   (value) => value === ""
                  // ) ||
                  // Object.values(editPatient?.first_name).every(
                  //   (value) => value === ""
                  // ) ||
                  // Object.values(editPatient?.birthdate).every(
                  //   (value) => value === ""
                  // ) ||
                  // Object.values(editPatient?.sex).every((value) => value === "")
                  !editPatient ||
                  !editPatient.last_name?.trim() ||
                  !editPatient.first_name?.trim() ||
                  !editPatient.middle_name?.trim() ||
                  !editPatient.country?.trim() ||
                  !editPatient.province?.trim() ||
                  !editPatient.city?.trim() ||
                  !editPatient.street?.trim() ||
                  !editPatient.zip_code?.trim() ||
                  !editPatient.birthdate?.trim() ||
                  !editPatient.sex ||
                  !editPatient.civil_status ||
                  !editPatient.contact_no?.trim() ||
                  editPatient.contact_no?.length <= 3 ||
                  emergencyContactRequired
                }
              />
            </div>
          </div>
        </div>
      </div>
      {/* Camera Modal */}
      <Modal
        title="Take Patient Photo"
        open={isCameraModalVisible}
        onCancel={() => {
          setIsCameraModalVisible(false);
          setCapturedImage(null);
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setIsCameraModalVisible(false);
              setCapturedImage(null);
            }}
          >
            Cancel
          </Button>,
          capturedImage ? (
            <Button key="retake" onClick={retakePhoto}>
              Retake
            </Button>
          ) : (
            <Button key="capture" type="primary" onClick={capturePhoto}>
              Capture Photo
            </Button>
          ),
          capturedImage && (
            <Button key="use" type="primary" onClick={usePhoto}>
              Use This Photo
            </Button>
          ),
        ]}
        width={700}
      >
        {/* Camera Selection Dropdown */}
        {availableCameras.length > 1 && (
          <div style={{ marginBottom: "16px" }}>
            <Select
              style={{ width: "100%" }}
              value={selectedCamera}
              onChange={handleCameraChange}
              suffixIcon={<SettingOutlined />}
              placeholder="Select Camera"
            >
              {availableCameras.map((camera) => (
                <Select.Option key={camera.deviceId} value={camera.deviceId}>
                  {camera.label ||
                    `Camera ${availableCameras.indexOf(camera) + 1}`}
                </Select.Option>
              ))}
            </Select>
          </div>
        )}

        <div style={{ textAlign: "center" }}>
          {!capturedImage ? (
            <Webcam
              audio={false}
              ref={webcamRef}
              screenshotFormat="image/jpeg"
              videoConstraints={videoConstraints}
              style={{
                width: "100%",
                maxHeight: "500px",
                border: "2px dashed #d9d9d9",
                borderRadius: "8px",
              }}
              onUserMediaError={(error) => {
                console.error("Webcam error:", error);
                // Fallback to default camera if selected camera fails
                if (availableCameras.length > 0) {
                  setSelectedCamera(availableCameras[0].deviceId);
                }
              }}
            />
          ) : (
            <img
              src={capturedImage}
              alt="Captured"
              style={{
                width: "100%",
                maxHeight: "500px",
                objectFit: "contain",
                border: "2px solid #1890ff",
                borderRadius: "8px",
              }}
            />
          )}
        </div>

        {/* Camera instructions */}
        {availableCameras.length === 0 && (
          <div
            style={{
              padding: "16px",
              backgroundColor: "#fff2e8",
              borderRadius: "4px",
              marginTop: "16px",
              textAlign: "center",
            }}
          >
            <p>
              No cameras detected. Please ensure your webcam is connected and
              permissions are granted.
            </p>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default EditPatient;
